import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { hostError, hostsApi, type Host, type InstallInfo, type NodeInfo } from '../../api'
import { t, type Key } from '../../i18n'
import { NODES_KEY, useProvisioning } from '../Nodes'
import { HOSTS_KEY, hostKey } from '../provisioning'
import { newRequestId } from '../Hosts'
import { SWITCH_ID, SwitchDialog } from '../RestartNodeDialog'
import { Button, CodeCells, Dialog, Icon } from '../../ui'
import { relayKeys } from './api'
import { actionError } from './confirm'
import { plural } from './format'
import { COMPONENTS, Num, releaseVersion, SwitchToast, switchBlock, switchDone, tn, type Outcome, type SwitchService } from './nodeSwitch'
import { pushOperatorEvent } from './operatorEvents'
import { CODE_TRIES, codeOf, codeReady, emptyCode, holdersBlock, hostBlock, logicPair, publishersOn } from './updateGate'

/*
 * The node card's update block (HOLD-A8): the node's layout, its components'
 * versions, the active logic color and generation, and the actions —
 * Restart node, Update logic, Update agent (the host agent's switch: the broadcasts
 * stay on air, TOTP, no typed word) and Update holders (the release, only on a
 * freed node). On a monolith Restart node and Update logic are off with the reason;
 * Update agent works on both layouts. The node chip is never painted DRAINING or
 * UPDATING by these.
 */

/** Current and next logic, monospaced. No next version means the center does not change it here. */
export function LogicVersion({ n, host, empty = '—' }: { n: NodeInfo; host?: Host | null; empty?: string }) {
  const prov = useProvisioning()
  const { current, next } = logicPair(n, host, prov.keys.data?.install, empty)
  return (
    <span className="font-mono tabular-nums slashed-zero" data-testid={`node-logic-${n.id}`} data-next={next ?? ''}>
      {current}
      {next ? ` → ${next}` : ''}
    </span>
  )
}

/** Layout, active color and generation, every component's version (→ the release's when it changes here). */
export function NodeLayout({ host, info }: { host: Host; info?: InstallInfo }) {
  const layout = host.layout === 'split' ? t('node.layout.split') : host.layout === 'monolith' ? t('node.layout.monolith') : t('node.layout.unknown')
  const comps = COMPONENTS.filter((c) => host.components?.[c]?.version || host.components?.[c]?.image || (c === 'agent' && host.agent_version))
  return (
    <div className="mt-4 flex flex-col gap-2" data-testid="node-layout" data-layout={host.layout ?? ''}>
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <div className="flex gap-2">
          <dt className="text-muted">{t('node.layout')}</dt>
          <dd className="text-text-soft" data-testid="node-layout-kind">
            {layout}
          </dd>
        </div>
        {host.layout === 'split' && host.active_logic && (
          <div className="flex gap-2">
            <dt className="text-muted">{t('node.activeLogic')}</dt>
            <dd className="text-text-soft" data-testid="node-active-logic">
              <Num>{host.active_logic}</Num>
              {host.generation !== undefined && <> · {tn('node.generation', { n: host.generation })}</>}
            </dd>
          </div>
        )}
      </dl>
      {comps.length > 0 && (
        <table className="sx-table compact max-w-[560px]" data-testid="node-components" aria-label={t('node.components')}>
          <tbody>
            {comps.map((c) => {
              const now = host.components?.[c]?.version || (c === 'agent' ? host.agent_version : undefined) || host.components?.[c]?.image?.split(':').pop() || '—'
              const next = host.changes?.includes(c) ? releaseVersion(info, c) : undefined
              return (
                <tr key={c} data-testid={`node-comp-${c}`} data-changes={next ? 'true' : 'false'}>
                  <td className="w-40 text-muted">{t(`node.comp.${c}` as Key)}</td>
                  <td className="num !text-left">
                    {now}
                    {next && next !== now ? ` → ${next}` : ''}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

type Which = SwitchService | 'holders'



export function NodeUpdate({ n, host, hostsOn }: { n: NodeInfo; host: Host | null | undefined; hostsOn: boolean }) {
  const qc = useQueryClient()
  const prov = useProvisioning()
  const info = prov.keys.data?.install
  const [which, setWhich] = useState<Which | null>(null)
  const [toast, setToast] = useState<{ state: Outcome['state'] | 'idle'; title: ReactNode } | null>(null)
  const refs: Record<Which, RefObject<HTMLButtonElement | null>> = {
    restart: useRef<HTMLButtonElement>(null),
    logic: useRef<HTMLButtonElement>(null),
    agent: useRef<HTMLButtonElement>(null),
    holders: useRef<HTMLButtonElement>(null),
  }
  const behind = info?.release_no !== undefined && host?.release_no !== undefined && info.release_no > host.release_no
  const base = hostBlock(n, host, hostsOn)
  const why: Record<Which, string> = {
    restart: base || switchBlock('restart', host, info) || (behind ? t('node.why.behind') : ''),
    logic: base || switchBlock('logic', host, info),
    agent: base || switchBlock('agent', host, info),
    holders: holdersBlock(n, host, hostsOn),
  }
  const pair = logicPair(n, host, info)
  const streams = n.streams ?? 0

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: relayKeys.node(n.id) })
    void qc.invalidateQueries({ queryKey: NODES_KEY })
    void qc.invalidateQueries({ queryKey: HOSTS_KEY })
    if (host) void qc.invalidateQueries({ queryKey: hostKey(host.id) })
  }

  const refused = (text: string) => {
    pushOperatorEvent({ at: new Date().toISOString(), nodeId: n.id, name: n.name, text })
    return text
  }

  if (n.status === 'revoked') return null

  const action = (w: Which, label: string) => {
    const testId = w === 'holders' ? 'update-holders' : w === 'restart' ? 'node-restart' : SWITCH_ID[w]
    return (
      <div key={w} className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button ref={refs[w]} size="sm" className="max-md:!h-[44px]" data-testid={testId} disabled={!!why[w]} onClick={() => setWhich(w)}>
          {label}
        </Button>
        {why[w] && (
          <span className="text-xs text-muted" data-testid={`${testId}-why`}>
            {why[w]}
          </span>
        )}
      </div>
    )
  }

  const from = (w: SwitchService) => (w === 'agent' ? host?.components?.agent?.version || host?.agent_version : pair.current)
  const to = (w: SwitchService) => (w === 'agent' ? releaseVersion(info, 'agent') : pair.next)

  return (
    <div className="flex flex-col" data-testid="node-update">
      {host && hostsOn && <NodeLayout host={host} info={info} />}
      <div className="mt-4 flex flex-col gap-2">
        {action('restart', t('node.restart'))}
        {action('logic', t('node.updateLogic'))}
        {action('agent', t('node.updateAgent'))}
        {action('holders', t('node.updateHolders'))}
      </div>
      {which && which !== 'holders' && host && (
        <SwitchDialog
          service={which}
          hostId={host.id}
          name={n.name}
          releaseNo={host.release_no}
          streams={streams}
          from={from(which)}
          to={to(which)}
          returnFocus={refs[which]}
          onClose={() => setWhich(null)}
          onRefused={refused}
          onDone={(c) => {
            const w = which
            setWhich(null)
            setToast(switchDone(w, c, { nodeId: n.id, name: n.name, release: host.release_no, streams, version: to(w) }))
            refresh()
          }}
        />
      )}
      {which === 'holders' && host && (
        <HoldersDialog
          n={n}
          host={host}
          version={info?.image_tag || (info?.release_no !== undefined ? `#${info.release_no}` : '')}
          returnFocus={refs.holders}
          onClose={() => setWhich(null)}
          onDone={(version) => {
            setWhich(null)
            const left = publishersOn(n)
            setToast({
              state: 'idle',
              title: (
                <>
                  Holders on <Num>{n.name}</Num> are <Num>{version}</Num>, with <Num>{left}</Num> {left === 1 ? 'stream' : 'streams'} on this node.
                </>
              ),
            })
            pushOperatorEvent({ at: new Date().toISOString(), nodeId: n.id, name: n.name, text: `Holders are ${version}, with ${plural(left, 'stream', 'streams')} on this node.` })
            refresh()
          }}
          onRefused={refused}
        />
      )}
      {toast && <SwitchToast o={toast} testId="node-update-toast" onClose={() => setToast(null)} />}
    </div>
  )
}

/**
 * Update holders: the center's release (deploy) on a freed node — drained, no
 * publisher. The sockets on this node close and open again: ConfirmDialog level 3,
 * danger action (AUDIT PNU-01).
 */
function HoldersDialog({
  n,
  host,
  version,
  returnFocus,
  onClose,
  onDone,
  onRefused,
}: {
  n: NodeInfo
  host: Host
  version: string
  returnFocus: RefObject<HTMLButtonElement | null>
  onClose: () => void
  onDone: (version: string) => void
  onRefused: (text: string) => string
}) {
  const titleId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [cells, setCells] = useState(emptyCode)
  const [busy, setBusy] = useState(false)
  const [tries, setTries] = useState(CODE_TRIES)
  const [codeError, setCodeError] = useState('')
  const [formError, setFormError] = useState('')
  const left = publishersOn(n)

  const closeRef = useRef(onClose)
  const backRef = useRef(returnFocus)
  closeRef.current = onClose
  backRef.current = returnFocus
  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      backRef.current.current?.focus()
    }
  }, [titleId])

  const submit = async () => {
    if (!codeReady(cells) || busy || tries <= 0) return
    setBusy(true)
    setCodeError('')
    setFormError('')
    try {
      const c = await hostsApi.command(host.id, { kind: 'deploy', totp: codeOf(cells), request_id: newRequestId() })
      if (c.state !== 'ok') {
        const text = onRefused('Holders update did not run. Nothing changed.')
        setFormError(c.error ? `${text} ${c.error}` : text)
        return
      }
      const reported = typeof c.data?.image_tag === 'string' && c.data.image_tag ? c.data.image_tag : version
      onDone(reported)
    } catch (e) {
      const h = hostError(e)
      if (h?.code === 'totp_invalid') {
        const left = tries - 1
        setTries(left)
        setCodeError(left > 0 ? t(left === 1 ? 'node.wrongCode.one' : 'node.wrongCode.many', { n: left }) : t('node.locked'))
        return
      }
      if (h?.code === 'totp_locked') {
        setTries(0)
        setCodeError(h.retry_after_s ? t('node.lockedFor', { n: Math.ceil(h.retry_after_s / 60) }) : t('node.locked'))
        return
      }
      if (h?.code === 'publishers_present') {
        setFormError(onRefused(t('node.why.free')))
        return
      }
      setFormError(onRefused(actionError(e, 'Update holders')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      id={titleId}
      modal
      busy={busy}
      testId="update-holders-dialog"
      state="fail"
      title={
        <>
          Update holders on <span className="font-mono">{n.name}</span>?
        </>
      }
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" data-testid="update-holders-cancel" onClick={onClose}>
            {t('node.cancel')}
          </Button>
          <Button variant="danger" busy={busy} disabled={!codeReady(cells) || tries <= 0} data-testid="update-holders-confirm" onClick={() => void submit()}>
            {busy ? 'Updating holders…' : t('node.updateHolders')}
          </Button>
        </>
      }
    >
      <ul>
        <li>
          Node <Num>{n.name}</Num>
        </li>
        <li>
          <Num>{left}</Num> {left === 1 ? 'stream' : 'streams'} on this node
        </li>
        <li>Sockets on this node close and open again</li>
      </ul>
      <CodeCells id="update-holders-code" label={t('node.code')} value={cells} invalid={!!codeError} onChange={setCells} />
      {codeError && (
        <span className="flex items-center gap-1.5 text-xs text-fail" role="alert" data-testid="update-holders-code-error">
          <Icon name="state-fail" size={12} />
          {codeError}
        </span>
      )}
      {formError && (
        <p className="text-[13px] text-text" role="alert" data-testid="update-holders-error">
          {formError}
        </p>
      )}
    </Dialog>
  )
}
